import type { ComposerState } from '@/lib/campaign-composer-form';
import type { ScheduleChoice } from '@/lib/campaign-schedule';

/**
 * The composer's unsent work, kept in this browser so closing the tab or
 * leaving by mistake loses nothing. One draft per store; cleared once the
 * campaign is created or the merchant discards it.
 */
export interface CampaignDraft {
  savedAt: string;
  state: ComposerState;
}

const VERSION = 1;
const key = (tenantSlug: string) => `korza.campaignDraft.${tenantSlug}`;

export function saveCampaignDraft(
  storage: Storage,
  tenantSlug: string,
  state: ComposerState,
  savedAt: Date
): void {
  try {
    storage.setItem(
      key(tenantSlug),
      JSON.stringify({
        version: VERSION,
        savedAt: savedAt.toISOString(),
        state,
      })
    );
  } catch {
    // Storage full or blocked: the draft is a convenience, never an error.
  }
}

export function clearCampaignDraft(storage: Storage, tenantSlug: string) {
  try {
    storage.removeItem(key(tenantSlug));
  } catch {
    // Same as above.
  }
}

/** The saved draft, or null when there is none or it is not one we wrote. */
export function loadCampaignDraft(
  storage: Storage,
  tenantSlug: string
): CampaignDraft | null {
  let parsed: unknown;
  try {
    const raw = storage.getItem(key(tenantSlug));
    if (raw === null) return null;
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.version !== VERSION) return null;
  const { savedAt, state } = parsed;
  if (typeof savedAt !== 'string' || !isRecord(state)) return null;

  const text = (field: string) =>
    typeof state[field] === 'string' ? state[field] : null;
  const title = text('title');
  const body = text('body');
  const link = text('link');
  const linkPath = text('linkPath');
  const timezone = text('timezone');
  const name = text('name');
  const utmCampaign = text('utmCampaign');
  const schedule = toSchedule(state.schedule);
  if (
    title === null ||
    body === null ||
    link === null ||
    linkPath === null ||
    timezone === null ||
    name === null ||
    utmCampaign === null ||
    schedule === undefined ||
    (state.linkMode !== 'home' && state.linkMode !== 'page')
  ) {
    return null;
  }
  return {
    savedAt,
    state: {
      title,
      body,
      link,
      linkMode: state.linkMode,
      linkPath,
      schedule,
      timezone,
      name,
      nameEdited: state.nameEdited === true,
      utmCampaign,
      utmEdited: state.utmEdited === true,
      audienceId:
        typeof state.audienceId === 'string' ? state.audienceId : null,
      trackingOpen: false,
      utmAlreadyUsed: false,
      dirty: true,
    },
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The schedule, null for none, undefined when it is not a valid one. */
function toSchedule(value: unknown): ScheduleChoice | null | undefined {
  if (value === null) return null;
  if (!isRecord(value)) return undefined;
  switch (value.kind) {
    case 'now':
    case 'todayEvening':
    case 'tomorrowMorning':
      return { kind: value.kind };
    case 'custom':
      return typeof value.date === 'string' && typeof value.time === 'string'
        ? { kind: 'custom', date: value.date, time: value.time }
        : undefined;
    default:
      return undefined;
  }
}

/** This browser's localStorage, or null where it is blocked. */
export function browserStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
